// client/src/hooks/useLeaderboard.js
// Leaderboard = students ranked by the camp's metric (BALANCE / EARNED_TOTAL /
// EARNED_SINCE). The saved metric comes back with every response as
// `settings`; passing `view` previews another metric without saving it.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const key = (campId) => ['camps', campId, 'leaderboard'];

/**
 * @param {string} campId
 * @param {{ metric?: string, since?: string|null } | null} view
 *   null → the camp's saved metric. Response: { leaderboard, metric, since, settings }
 */
export function useLeaderboard(campId, view = null) {
  const query = {};
  if (view?.metric) query.metric = view.metric;
  if (view?.metric === 'EARNED_SINCE' && view.since) query.since = view.since;
  const ready = !view || view.metric !== 'EARNED_SINCE' || !!view.since;
  return useQuery({
    queryKey: [...key(campId), query],
    queryFn: () => api.get(`/api/camps/${campId}/leaderboard`, { query }),
    enabled: !!campId && ready,
    placeholderData: (prev) => prev,
    // Leaderboard is read-heavy; cache fresh for a minute to absorb F5 spam.
    staleTime: 60_000,
  });
}

/** PUT /leaderboard/settings — the metric everyone sees from now on. */
export function useSaveLeaderboardSettings(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ metric, since }) =>
      api.put(`/api/camps/${campId}/leaderboard/settings`, {
        metric,
        since: metric === 'EARNED_SINCE' ? since : null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(campId) }),
  });
}
